import { useState, useEffect } from "react";
import {
	DialogContent,
	Typography,
	Divider,
	Box,
	Paper,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { SwapHorizOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useTranslation } from "react-i18next";
import { canExportPdf } from "../../util/access";

// Simple date formatter (you can replace with your own util)
const formatDate = (dateStr: string, style: "short" | "full" = "short") => {
	if (!dateStr) return "—";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "long",
		year: "numeric",
		...(style === "full" && { weekday: "long" }),
	});
};

const formatNumber = (num: number | undefined) => {
	if (num === undefined || isNaN(num)) return "—";
	return num.toLocaleString("en-US");
};

interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any; // usually contains .batch
}

const ConversionReport = ({ isOpen, toggle, bundle }: Props) => {
	const { api, access } = useAuth();
	const { setLoadingMessage, queueReRun } = useUI();
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();

	const [record, setRecord] = useState<any>(null);


	const getConversionTypeLabel = (type?: string) => {
		switch (type) {
			case "KIT":
				return t("common:conversion.types.kit");
			case "DE-KIT":
				return t("common:conversion.types.dekit");
			case "SWAP":
				return t("common:conversion.types.swap");
			default:
				return t("common:conversion.types.unknown");
		}
	};

	const fetchData = async () => {
		if (!bundle?.batch) return;

		setLoadingMessage(t("common:report.loadingReport"));
		try {
			const res = await api.get(`conversions/${bundle.batch}`);
			if (res?.status === 200) {
				setRecord(res.data);
			} else if (res?.status === 404) {
				toast.error(t("common:report.conversionNotFound"));
			} else {
				toast.error(t("common:report.failedToLoadReport"));
				console.error("API error:", res);
			}
		} catch (err) {
			console.error("Fetch error:", err);
			toast.error(t("common:report.errorConnecting"));
		} finally {
			setLoadingMessage(null);
		}
	};

	useEffect(() => {
		if (isOpen && bundle?.batch) {
			fetchData();
		}
		// Optional: clear record when modal closes
		return () => {
			if (!isOpen) setRecord(null);
		};
	}, [isOpen, bundle?.batch]);

	const handleExport = async () => {
		if (!bundle?.batch || !canExportPdf(access, "conversion")) return;

		setLoadingMessage(t("common:report.loadingReport"));
		try {
			const res = await api.get(`reports/conversion/${bundle.batch}`);
			if (res?.status === 200 && res.data?.data) {
				const pdfData = atob(res.data.data);
				const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], {
					type: "application/pdf",
				});
				window.open(URL.createObjectURL(blob));
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(() => handleExport());
			} else {
				toast.error(t("common:report.failedToLoadReport"));
			}
		} catch (err) {
			console.error("Export error:", err);
			toast.error(t("common:report.errorConnecting"));
		} finally {
			setLoadingMessage(null);
		}
	};

	if (!record) {
		return (
			<MoleDialog open={isOpen} onClose={toggle} maxWidth="sm" fullWidth themeColor={themeColor}>
				<MoleDialogHeader
					title={t("common:conversionReport.title")}
					icon={<SwapHorizOutlined />}
					onClose={toggle}
					themeColor={themeColor}
				/>
				<DialogContent sx={{ py: 6, textAlign: "center" }}>
					<Typography color="text.secondary">{t("common:report.loadingReport")}</Typography>
				</DialogContent>
			</MoleDialog>
		);
	}

	return (
		<MoleDialog open={isOpen} onClose={toggle} maxWidth="md" fullWidth themeColor={themeColor} paperOptions={{ maxHeight: "95vh" }}>
			<MoleDialogHeader
				eyebrow={t("common:conversionReport.title")}
				title={record.reference || "—"}
				icon={<SwapHorizOutlined />}
				onClose={toggle}
				themeColor={themeColor}
			/>

			<DialogContent dividers sx={{ bgcolor: "background.default", py: 4 }}>
				<Paper
					elevation={0}
					sx={{
						p: 4,
						bgcolor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						maxWidth: 900,
						mx: "auto",
						"@media print": {
							boxShadow: "none",
							border: "none",
						},
					}}
				>
					{/* Header Info */}
					<Box sx={{ mb: 4 }}>
						<Typography variant="h5" fontWeight={600} gutterBottom>
						{t("common:conversionReport.itemConversion")} - {getConversionTypeLabel(record.type)}
					</Typography>
					<Typography variant="subtitle2" color="text.secondary">
						{t("common:conversionReport.refNo")}: <strong>{record.reference || "—"}</strong>
						</Typography>
					</Box>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
							gap: 3,
							mb: 4,
						}}
					>
						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								{t("common:conversionReport.date")}
							</Typography>
							<Typography>{formatDate(record.date, "full")}</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								{t("common:conversionReport.user")}
							</Typography>
							<Typography>{record.user || "—"}</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								{t("common:conversionReport.depot")}
							</Typography>
							<Typography>{record.depot || "—"}</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								{t("common:conversionReport.observer")}
							</Typography>
							<Typography>{record.client || "—"}</Typography>
						</Box>
					</Box>

					<Divider sx={{ my: 3 }} />

					{/* Conversion Details */}
					<Box sx={{ mb: 4 }}>
						<Typography variant="h6" gutterBottom sx={{ color: "primary.main" }}>
						{t("common:conversionReport.details")}
						</Typography>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
								gap: 4,
								alignItems: "start",
							}}
						>
							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.sourceItem")}
								</Typography>
								<Typography variant="body1" fontWeight={500}>
									{record.source_item || "—"}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.destItem")}
								</Typography>
								<Typography variant="body1" fontWeight={500}>
									{record.dest_item || "—"}
								</Typography>
							</Box>
						</Box>

						<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.quantityUsed")}
							</Typography>
							<Typography variant="h6">
								{formatNumber(record.quantity)} {t("common:conversionReport.pcs")}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.factor")}
								</Typography>
								<Typography variant="h6">
									{record.type === "KIT" ? `${record.factor} : 1`
										: record.type === "DE-KIT" ? `1 : ${record.factor}`
											: record.type === "SWAP" ? "1 : 1"
												: t("common:conversion.types.unknown")}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.resultingQty")}
								</Typography>
								<Typography variant="h5" color="success.main" fontWeight={600}>
								{formatNumber(record.total_qty)} {t("common:conversionReport.pcs")}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
								{t("common:conversionReport.totalValue")}
								</Typography>
								<Typography variant="h5" color="primary.main" fontWeight={600}>
									{formatNumber(record.total_amt)}
								</Typography>
							</Box>
						</Box>
					</Box>

					{record.description && (
						<>
							<Divider sx={{ my: 3 }} />
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									{t("common:conversionReport.notes")}
								</Typography>
								<Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
									{record.description}
								</Typography>
							</Box>
						</>
					)}
				</Paper>
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.close")}</MoleCancelButton>
				{canExportPdf(access, "conversion") && (
					<MolePrimaryButton
						startIcon={<FontAwesomeIcon icon={faFilePdf} />}
						onClick={handleExport}
						disabled={!record}
					>
						{t("common:ui.buttons.export")}
					</MolePrimaryButton>
				)}
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default ConversionReport;